
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import PublicBrowse from '../components/PublicBrowse';
import Footer from '../components/Footer';
import { Helmet } from "react-helmet-async";


const Landing = () => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [backgroundImage, setBackgroundImage] = useState('');

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Lazy load background image
  useEffect(() => {
    const imageUrl = isMobile ? '/images/hero-bg-mobile.jpeg' : '/images/hero-bg.jpeg';

    // Create a new image object to preload
    const img = new Image();

    img.onload = () => {
      setBackgroundImage(imageUrl);
      // Add a small delay for smooth transition
      setTimeout(() => {
        setImageLoaded(true);
      }, 100);
    };

    img.onerror = () => {
      // Fallback: still show content even if image fails to load
      setImageLoaded(true);
    };

    // Start loading the image
    img.src = imageUrl;
  }, [isMobile]);

  // Add preload hints for better performance
  useEffect(() => {
    // Preload images for better performance
    const desktopLink = document.createElement('link');
    desktopLink.rel = 'preload';
    desktopLink.as = 'image';
    desktopLink.href = '/images/hero-bg.jpeg';
    document.head.appendChild(desktopLink);

    const mobileLink = document.createElement('link');
    mobileLink.rel = 'preload';
    mobileLink.as = 'image';
    mobileLink.href = '/images/hero-bg-mobile.jpeg';
    document.head.appendChild(mobileLink);

    // Cleanup
    return () => {
      document.head.removeChild(desktopLink);
      document.head.removeChild(mobileLink);
    };
  }, []);

  return (
    <>
      {/* Responsive Background Image Styles with Lazy Loading */}
      <Helmet>
        <title>VestrioZ - Rent Designer Fashion & Jewellery Online | Save 80% on Premium Clothing</title>
        <meta
          name="description"
          content="India's #1 fashion sharing platform. Rent designer clothing & jewellery at 80% less cost. Premium ethnic wear, party dresses, wedding outfits & handcrafted items. Join 10,000+ users!"
        />
        <meta name="keywords" content="fashion rental India, rent designer clothes, jewellery rental, sustainable fashion, clothing rental platform, designer dress rental, ethnic wear rental, party wear rental, wedding outfit rental, handcraft rental, VestrioZ" />
        <link rel="canonical" href="https://vestrioz.com/" />
        <meta property="og:title" content="VestrioZ - Rent Designer Fashion & Jewellery Online" />
        <meta property="og:description" content="India's #1 fashion sharing platform. Rent designer clothing & jewellery at 80% less cost. Join 10,000+ users!" />
        <meta property="og:url" content="https://vestrioz.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://vestrioz.com/VESTRIOZ.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="VestrioZ - Rent Designer Fashion & Jewellery Online" />
        <meta name="twitter:description" content="India's #1 fashion sharing platform. Rent designer clothing & jewellery at 80% less cost." />
        <meta name="twitter:image" content="https://vestrioz.com/VESTRIOZ.png" />
        <link rel="icon" href="/favicon.ico" />
        <link rel="apple-touch-icon" href="/VESTRIOZ.png" />
      </Helmet>
      <style>{`
        .hero-bg {
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          transition: opacity 0.6s ease-in-out, background-image 0.6s ease-in-out;
        }
        
        .hero-bg.loading {
          background-image: none;
          animation: shimmer 2s infinite;
        }
        
        .hero-bg.loaded {
          opacity: 1;
        }
        
        @keyframes shimmer {
          0% {
            background-position: -1000px 0;
          }
          100% {
            background-position: 1000px 0;
          }
        }
        
        .shimmer-bg {
          background: linear-gradient(
            90deg,
            rgba(34, 40, 49, 0.9) 0%,
            rgba(57, 62, 70, 0.7) 25%,
            rgba(148, 137, 121, 0.6) 50%,
            rgba(57, 62, 70, 0.7) 75%,
            rgba(34, 40, 49, 0.9) 100%
          );
          background-size: 1000px 100%;
          animation: shimmer 2.5s ease-in-out infinite;
        }
        
        .pulse-glow {
          animation: pulseGlow 2s ease-in-out infinite alternate;
        }
        
        @keyframes pulseGlow {
          from {
            box-shadow: 0 0 20px rgba(148, 137, 121, 0.3);
          }
          to {
            box-shadow: 0 0 40px rgba(148, 137, 121, 0.6);
          }
        }
        
        .fade-in {
          animation: fadeIn 0.6s ease-in-out;
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
      `}</style>

      <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #DFD0B8 0%, #f5f1e8 100%)' }}>
        {/* Navigation */}
        <nav className="backdrop-blur-md shadow-xl border-b sticky top-0 z-50" style={{ background: 'rgba(223, 208, 184, 0.95)', borderColor: 'rgba(148, 137, 121, 0.3)' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-2">
              <div className="flex items-center">
                <div className="flex items-center">
                  <img
                    src="/images/VESTRIOZ.png"
                    alt="VestrioZ Logo"
                    className="h-14 sm:h-16 w-auto object-contain"
                    onError={(e) => {
                      // Fallback to icon if logo image fails to load
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="h-14 sm:h-16 w-14 sm:w-16 rounded-xl flex items-center justify-center shadow-lg" style={{ backgroundColor: '#222831', display: 'none' }}>
                    <i className="fas fa-exchange-alt text-white text-xl sm:text-2xl"></i>
                  </div>
                </div>
              </div>
              <div className="flex space-x-2 sm:space-x-3">
                <Link
                  to="/login"
                  className="btn-primary text-xs sm:text-base px-2 sm:px-4 py-2 flex items-center"
                >
                  <i className="fas fa-sign-in-alt mr-1"></i>
                  <span>Login</span>
                </Link>
                <Link
                  to="/signup"
                  className="btn-outline text-xs sm:text-base px-2 sm:px-4 py-2 flex items-center"
                >
                  <i className="fas fa-user-plus mr-1"></i>
                  <span>Sign Up</span>
                </Link>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <div
          className={`text-white relative overflow-hidden min-h-screen flex items-center hero-bg ${imageLoaded ? 'loaded fade-in' : 'loading'
            }`}
          style={{
            backgroundColor: '#222831',
            backgroundImage: imageLoaded && backgroundImage ? `url(${backgroundImage})` : 'none'
          }}
        >
          {/* Loading shimmer effect */}
          {!imageLoaded && (
            <div className="absolute inset-0 shimmer-bg"></div>
          )}

          {/* Enhanced Loading skeleton for content */}
          {!imageLoaded && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center space-y-6 animate-pulse">
                {/* Logo skeleton with glow effect */}
                <div className="w-20 h-20 bg-white/20 rounded-2xl mx-auto pulse-glow relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-pulse"></div>
                </div>

                {/* Title skeleton */}
                <div className="space-y-3">
                  <div className="h-10 bg-white/20 rounded-lg w-80 mx-auto relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-pulse"></div>
                  </div>
                  <div className="h-10 bg-white/20 rounded-lg w-64 mx-auto relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-pulse"></div>
                  </div>
                </div>

                {/* Description skeleton */}
                <div className="space-y-2">
                  <div className="h-5 bg-white/15 rounded w-96 mx-auto relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-pulse"></div>
                  </div>
                  <div className="h-5 bg-white/15 rounded w-80 mx-auto relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-pulse"></div>
                  </div>
                </div>

                {/* Buttons skeleton */}
                <div className="flex gap-4 justify-center pt-4">
                  <div className="h-14 bg-white/20 rounded-xl w-40 relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-pulse"></div>
                  </div>
                  <div className="h-14 bg-white/20 rounded-xl w-40 relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-pulse"></div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Light overlay for better text readability while keeping image visible */}
          <div
            className={`absolute inset-0 transition-opacity duration-600 ${imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
            style={{ backgroundColor: 'rgba(34, 40, 49, 0.3)' }}
          ></div>

          {/* Subtle gradient overlay */}
          <div
            className={`absolute inset-0 transition-opacity duration-600 ${imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
            style={{
              background: 'linear-gradient(135deg, rgba(34, 40, 49, 0.2) 0%, rgba(57, 62, 70, 0.1) 50%, rgba(148, 137, 121, 0.1) 100%)'
            }}
          ></div>
          {/* Main Content */}
          <div
            className={`relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 md:py-24 lg:py-32 w-full transition-all duration-600 ${imageLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
          >
            <div className="text-center">
              <div className="mb-6 sm:mb-8">
                <div
                  className={`w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-2xl sm:rounded-3xl flex items-center justify-center mx-auto mb-4 sm:mb-6 transition-all duration-700 ${imageLoaded ? 'scale-100 rotate-0' : 'scale-75 rotate-12'
                    }`}
                >
                  <span
                    className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl"
                    style={{
                      color: '#DFD0B8',
                      textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                      background: 'linear-gradient(135deg, #DFD0B8 0%, #f5f1e8 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      fontWeight: 'bold',
                      letterSpacing: '0.05em'
                    }}
                  >
                    VestrioZ
                  </span>
                  <i
                    className="fas fa-exchange-alt text-white text-2xl sm:text-3xl md:text-4xl"
                    style={{ display: 'none' }}
                  ></i>
                </div>
              </div>

              <h1
                className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 leading-tight px-4 transition-all duration-700 delay-100 ${imageLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
              >
                Share, Rent & Sell <span
                  style={{
                    color: '#DFD0B8',
                    textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                    background: 'linear-gradient(135deg, #DFD0B8 0%, #f5f1e8 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    fontWeight: 'bold',
                    letterSpacing: '0.05em'
                  }}
                >
                  Style
                </span>
              </h1>

              <p
                className={`text-base sm:text-lg md:text-xl lg:text-2xl mb-6 sm:mb-8 opacity-90 max-w-xs sm:max-w-2xl md:max-w-3xl mx-auto leading-relaxed px-4 transition-all duration-700 delay-200 ${imageLoaded ? 'opacity-90 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
              >
                The ultimate platform for sharing fashion and jewellery. Rent or buy stunning clothing and jewellery,
                or earn money by renting out or selling items from your collection.
              </p>

              <div
                className={`flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 max-w-sm sm:max-w-md mx-auto px-4 transition-all duration-700 delay-300 ${imageLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
              >
                <Link
                  to="/signup"
                  className="px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-bold text-base sm:text-lg transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
                  style={{
                    background: '#DFD0B8',
                    color: '#222831',
                    border: 'none'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#d4c5a0'}
                  onMouseLeave={(e) => e.target.style.background = '#DFD0B8'}
                >
                  <i className="fas fa-user mr-2"></i>Get Started Free
                </Link>
                <Link
                  to="/browse"
                  className="border-2 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-bold text-base sm:text-lg transition-all duration-300 backdrop-blur-sm"
                  style={{
                    borderColor: '#DFD0B8',
                    color: '#DFD0B8'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#DFD0B8';
                    e.target.style.color = '#222831';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'transparent';
                    e.target.style.color = '#DFD0B8';
                  }}
                >
                  <i className="fas fa-eye mr-2"></i>Browse Items
                </Link>
              </div>
            </div>
          </div>

          {/* Enhanced Loading Progress Indicator */}
          {!imageLoaded && (
            <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
              <div className="flex flex-col items-center space-y-3">
                {/* Progress bar */}
                <div className="w-48 h-1 bg-white/20 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-white/50 to-white/80 rounded-full animate-pulse"></div>
                </div>

                {/* Loading dots and text */}
                <div className="flex items-center space-x-2 text-white/70">
                  <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: '#DFD0B8' }}></div>
                  <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: '#DFD0B8', animationDelay: '0.1s' }}></div>
                  <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: '#DFD0B8', animationDelay: '0.2s' }}></div>
                  <span className="ml-3 text-sm font-medium">Loading your fashion experience...</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Features Section */}
        <div 
          className="py-16 sm:py-20 md:py-24 relative overflow-hidden"
          style={{
            backgroundImage: 'url(/images/hero-bg-mobile.jpeg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          {/* Light overlay for better content readability */}
          <div className="absolute inset-0" style={{ backgroundColor: 'rgba(255, 255, 255, 0.92)' }}></div>
          
          {/* Subtle pattern overlay */}
          <div 
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(148, 137, 121, 0.15) 1px, transparent 0)',
              backgroundSize: '20px 20px'
            }}
          ></div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 sm:mb-16 md:mb-20">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 px-4" style={{ color: '#222831' }}>
                How VestrioZ Works
              </h2>
              <p className="text-lg sm:text-xl max-w-2xl mx-auto px-4" style={{ color: '#393E46' }}>
                Three simple steps to access premium fashion and jewellery - rent or buy at affordable prices
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-10 md:gap-12">
              <div className="text-center group px-4">
                <div className="w-16 h-16 sm:w-18 sm:h-18 md:w-20 md:h-20 rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6 shadow-xl group-hover:shadow-2xl transition-all duration-300 transform group-hover:-translate-y-2" style={{ backgroundColor: '#222831' }}>
                  <i className="fas fa-search text-2xl sm:text-3xl text-white"></i>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4" style={{ color: '#222831' }}>Discover & Browse</h3>
                <p className="leading-relaxed text-sm sm:text-base" style={{ color: '#393E46' }}>
                  Explore thousands of premium clothing items and jewellery pieces from verified users.
                  Find items for rent or purchase using smart filters for any occasion.
                </p>
              </div>

              <div className="text-center group px-4">
                <div className="w-16 h-16 sm:w-18 sm:h-18 md:w-20 md:h-20 rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6 shadow-xl group-hover:shadow-2xl transition-all duration-300 transform group-hover:-translate-y-2" style={{ backgroundColor: '#393E46' }}>
                  <i className="fas fa-comments text-2xl sm:text-3xl text-white"></i>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4" style={{ color: '#393E46' }}>Connect & Arrange</h3>
                <p className="leading-relaxed text-sm sm:text-base" style={{ color: '#393E46' }}>
                  Chat securely with item owners to discuss rental or purchase details, pickup arrangements,
                  and get personalized styling recommendations.
                </p>
              </div>

              <div className="text-center group px-4 sm:col-span-2 lg:col-span-1">
                <div className="w-16 h-16 sm:w-18 sm:h-18 md:w-20 md:h-20 rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6 shadow-xl group-hover:shadow-2xl transition-all duration-300 transform group-hover:-translate-y-2" style={{ backgroundColor: '#393E46' }}>
                  <i className="fas fa-star text-2xl sm:text-3xl text-white"></i>
                </div>

                <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4" style={{ color: '#393E46' }}>Rent or Buy & Shine</h3>

                <p className="leading-relaxed text-sm sm:text-base" style={{ color: '#393E46' }}>
                  Get your perfect outfit delivered or pick it up. Rent for special occasions or buy to keep forever.
                  Look amazing with flexible options that suit your needs!
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Public Browse Section */}
        <PublicBrowse />

        {/* About Us Section */}
        <div className="py-16 sm:py-20 md:py-24" style={{ background: 'linear-gradient(135deg, #f5f1e8 0%, #ede5d8 100%)' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 sm:mb-16 md:mb-20">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 px-4" style={{ color: '#222831' }}>
                About VestrioZ
              </h2>
              <p className="text-lg sm:text-xl max-w-3xl mx-auto leading-relaxed px-4" style={{ color: '#393E46' }}>
                We're revolutionizing how people access fashion by creating a sustainable,
                community-driven platform where style meets affordability and environmental consciousness.
              </p>
            </div>

            {/* Core Values */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mb-12 sm:mb-16 md:mb-20">
              <div className="backdrop-blur-sm rounded-2xl shadow-xl p-6 text-center card-hover border" style={{ background: 'rgba(255, 255, 255, 0.9)', borderColor: 'rgba(148, 137, 121, 0.2)' }}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg" style={{ backgroundColor: '#222831' }}>
                  <i className="fas fa-recycle text-white text-lg"></i>
                </div>
                <h3 className="text-lg font-bold mb-2" style={{ color: '#222831' }}>Sustainability</h3>
                <p className="text-sm leading-relaxed" style={{ color: '#393E46' }}>
                  Reducing fashion waste through circular economy and conscious consumption
                </p>
              </div>

              <div className="backdrop-blur-sm rounded-2xl shadow-xl p-6 text-center card-hover border" style={{ background: 'rgba(255, 255, 255, 0.9)', borderColor: 'rgba(148, 137, 121, 0.2)' }}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg" style={{ backgroundColor: '#393E46' }}>
                  <i className="fas fa-users text-white text-lg"></i>
                </div>
                <h3 className="text-lg font-bold mb-2" style={{ color: '#393E46' }}>Community</h3>
                <p className="text-sm leading-relaxed" style={{ color: '#393E46' }}>
                  Building connections through shared fashion experiences and mutual trust
                </p>
              </div>

              <div className="backdrop-blur-sm rounded-2xl shadow-xl p-6 text-center card-hover border" style={{ background: 'rgba(255, 255, 255, 0.9)', borderColor: 'rgba(148, 137, 121, 0.2)' }}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg" style={{ backgroundColor: '#393E46' }}>
                  <i className="fas fa-dollar-sign text-white text-lg"></i>
                </div>
                <h3 className="text-lg font-bold mb-2" style={{ color: '#393E46' }}>Affordability</h3>
                <p className="text-sm leading-relaxed" style={{ color: '#393E46' }}>
                  Making premium fashion accessible to everyone at fraction of retail prices
                </p>
              </div>

              <div className="backdrop-blur-sm rounded-2xl shadow-xl p-6 text-center card-hover border" style={{ background: 'rgba(255, 255, 255, 0.9)', borderColor: 'rgba(148, 137, 121, 0.2)' }}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg" style={{ backgroundColor: '#222831' }}>
                  <i className="fas fa-shield-alt text-white text-lg"></i>
                </div>
                <h3 className="text-lg font-bold mb-2" style={{ color: '#222831' }}>Trust & Safety</h3>
                <p className="text-sm leading-relaxed" style={{ color: '#393E46' }}>
                  Ensuring secure transactions and verified users for peace of mind
                </p>
              </div>
            </div>

            {/* Vision & Mission */}
            <div className="grid sm:grid-cols-1 md:grid-cols-2 gap-8 sm:gap-10 md:gap-12">
              <div className="backdrop-blur-sm rounded-2xl shadow-xl p-6 sm:p-8 border mx-4 sm:mx-0" style={{ background: 'rgba(255, 255, 255, 0.9)', borderColor: 'rgba(148, 137, 121, 0.2)' }}>
                <div className="text-center mb-4 sm:mb-6">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-lg" style={{ backgroundColor: '#222831' }}>
                    <i className="fas fa-eye text-lg sm:text-xl md:text-2xl text-white"></i>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold" style={{ color: '#222831' }}>Our Vision</h3>
                </div>
                <p className="leading-relaxed text-center text-sm sm:text-base" style={{ color: '#393E46' }}>
                  To revolutionize fashion consumption by creating a world where premium clothing and accessories
                  are shared, not owned. We envision a sustainable future where style is accessible to all,
                  waste is minimized, and communities thrive through shared resources.
                </p>
              </div>

              <div className="backdrop-blur-sm rounded-2xl shadow-xl p-6 sm:p-8 border mx-4 sm:mx-0" style={{ background: 'rgba(255, 255, 255, 0.9)', borderColor: 'rgba(148, 137, 121, 0.2)' }}>
                <div className="text-center mb-4 sm:mb-6">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-lg" style={{ backgroundColor: '#393E46' }}>
                    <i className="fas fa-bullseye text-lg sm:text-xl md:text-2xl text-white"></i>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold" style={{ color: '#393E46' }}>Our Mission</h3>
                </div>
                <p className="leading-relaxed text-center text-sm sm:text-base" style={{ color: '#393E46' }}>
                  To build the world's most trusted platform for fashion sharing, connecting people through
                  style while promoting sustainability. We empower individuals to monetize their wardrobes
                  and access premium fashion at affordable prices.
                </p>
              </div>
            </div>

            {/* What Makes Us Different */}
            <div className="mt-12 sm:mt-14 md:mt-16 backdrop-blur-sm rounded-2xl shadow-xl p-6 sm:p-8 border mx-4 sm:mx-0" style={{ background: 'rgba(255, 255, 255, 0.9)', borderColor: 'rgba(148, 137, 121, 0.2)' }}>
              <div className="text-center mb-6 sm:mb-8">
                <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-lg" style={{ backgroundColor: '#393E46' }}>
                  <i className="fas fa-star text-lg sm:text-xl md:text-2xl text-white"></i>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold mb-2 sm:mb-4" style={{ color: '#393E46' }}>What Makes Us Different</h3>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                <div className="text-center">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: 'rgba(148, 137, 121, 0.2)' }}>
                    <i className="fas fa-gem text-sm sm:text-base" style={{ color: '#948979' }}></i>
                  </div>
                  <h4 className="font-semibold mb-2 text-sm sm:text-base" style={{ color: '#393E46' }}>Premium Quality</h4>
                  <p className="text-xs sm:text-sm" style={{ color: '#393E46' }}>Curated collection of high-quality clothing and authentic jewellery pieces</p>
                </div>
                <div className="text-center">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: 'rgba(148, 137, 121, 0.2)' }}>
                    <i className="fas fa-shield-alt text-sm sm:text-base" style={{ color: '#948979' }}></i>
                  </div>
                  <h4 className="font-semibold mb-2 text-sm sm:text-base" style={{ color: '#222831' }}>Verified Users</h4>
                  <p className="text-xs sm:text-sm" style={{ color: '#393E46' }}>All users are verified for safety and trust in every transaction</p>
                </div>
                <div className="text-center sm:col-span-2 lg:col-span-1">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: 'rgba(148, 137, 121, 0.2)' }}>
                    <i className="fas fa-clock text-sm sm:text-base" style={{ color: '#948979' }}></i>
                  </div>
                  <h4 className="font-semibold mb-2 text-sm sm:text-base" style={{ color: '#222831' }}>Flexible Options</h4>
                  <p className="text-xs sm:text-sm" style={{ color: '#393E46' }}>Rent for any duration or buy items permanently - choose what works for you</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Pricing Section */}
        <div id="pricing" className="py-16 sm:py-20 md:py-24" style={{ backgroundColor: '#F6F1EB' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4" style={{ color: '#222831' }}>
                Simple, Transparent Pricing
              </h2>
              <p className="text-lg max-w-2xl mx-auto" style={{ color: '#393E46' }}>
                Choose the perfect plan for your needs. Start free and upgrade anytime!
              </p>
            </div>

            {/* Pricing Plans */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {/* FREE Plan */}
              <div className="bg-white rounded-2xl shadow-xl border-2 border-gray-200 p-6 relative flex flex-col">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-green-500 text-white px-4 py-1 rounded-full text-xs font-bold">
                    INCLUDED
                  </span>
                </div>

                {/* Header Section - Fixed Height */}
                <div className="text-center pt-4 h-32 flex flex-col justify-center">
                  <h3 className="text-2xl font-bold mb-2" style={{ color: '#222831' }}>FREE</h3>
                  <p className="text-sm" style={{ color: '#393E46' }}>Perfect for getting started</p>
                </div>

                {/* Price Section - Fixed Height */}
                <div className="text-center h-20 flex flex-col justify-center mb-4">
                  <div>
                    <span className="text-4xl font-bold" style={{ color: '#222831' }}>₹0</span>
                    <span className="text-sm" style={{ color: '#393E46' }}>/forever</span>
                  </div>
                </div>

                {/* Features Section - Fixed Height */}
                <div className="h-48 space-y-3 mb-6">
                  <div className="flex items-center">
                    <i className="fas fa-check text-green-500 w-5 mr-3"></i>
                    <span className="text-sm" style={{ color: '#222831' }}>3 Free Listings</span>
                  </div>
                  <div className="flex items-center">
                    <i className="fas fa-check text-green-500 w-5 mr-3"></i>
                    <span className="text-sm" style={{ color: '#222831' }}>30 Days Validity Each</span>
                  </div>
                  <div className="flex items-center">
                    <i className="fas fa-check text-green-500 w-5 mr-3"></i>
                    <span className="text-sm" style={{ color: '#222831' }}>Basic Support</span>
                  </div>
                  <div className="flex items-center">
                    <i className="fas fa-check text-green-500 w-5 mr-3"></i>
                    <span className="text-sm" style={{ color: '#222831' }}>Standard Visibility</span>
                  </div>
                  <div className="flex items-center opacity-30">
                    <i className="fas fa-times text-gray-400 w-5 mr-3"></i>
                    <span className="text-sm text-gray-400">Priority Support</span>
                  </div>
                  <div className="flex items-center opacity-30">
                    <i className="fas fa-times text-gray-400 w-5 mr-3"></i>
                    <span className="text-sm text-gray-400">Enhanced Visibility</span>
                  </div>
                </div>

                {/* Button Section - Fixed Height */}
                <div className="h-12 flex items-end">
                  <Link
                    to="/signup"
                    className="w-full py-3 rounded-xl font-bold text-sm text-center transition-all duration-300"
                    style={{ backgroundColor: '#222831', color: 'white' }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#393E46'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#222831'}
                  >
                    Get Started Free
                  </Link>
                </div>
              </div>

              {/* BASIC Plan */}
              <div className="bg-white rounded-2xl shadow-xl border-2 border-blue-200 p-6 relative flex flex-col">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-blue-500 text-white px-4 py-1 rounded-full text-xs font-bold">
                    AFFORDABLE
                  </span>
                </div>

                {/* Header Section - Fixed Height */}
                <div className="text-center pt-4 h-32 flex flex-col justify-center">
                  <h3 className="text-2xl font-bold mb-2" style={{ color: '#222831' }}>BASIC</h3>
                  <p className="text-sm" style={{ color: '#393E46' }}>Perfect for trying premium</p>
                </div>

                {/* Price Section - Fixed Height */}
                <div className="text-center h-20 flex flex-col justify-center mb-4">
                  <div>
                    <span className="text-4xl font-bold" style={{ color: '#222831' }}>₹11</span>
                    <span className="text-sm" style={{ color: '#393E46' }}>/one-time</span>
                  </div>
                  <div className="text-xs text-blue-600 mt-1">
                    Just ₹11 for 1 listing
                  </div>
                </div>

                {/* Features Section - Fixed Height */}
                <div className="h-48 space-y-3 mb-6">
                  <div className="flex items-center">
                    <i className="fas fa-check text-blue-500 w-5 mr-3"></i>
                    <span className="text-sm font-medium" style={{ color: '#222831' }}>1 Premium Listing</span>
                  </div>
                  <div className="flex items-center">
                    <i className="fas fa-check text-blue-500 w-5 mr-3"></i>
                    <span className="text-sm" style={{ color: '#222831' }}>30 Days Validity</span>
                  </div>
                  <div className="flex items-center">
                    <i className="fas fa-check text-blue-500 w-5 mr-3"></i>
                    <span className="text-sm" style={{ color: '#222831' }}>Priority Support</span>
                  </div>
                  <div className="flex items-center">
                    <i className="fas fa-check text-blue-500 w-5 mr-3"></i>
                    <span className="text-sm" style={{ color: '#222831' }}>Enhanced Visibility</span>
                  </div>
                  <div className="flex items-center">
                    <i className="fas fa-infinity text-blue-500 w-5 mr-3"></i>
                    <span className="text-sm" style={{ color: '#222831' }}>Credits Never Expire</span>
                  </div>
                  <div className="flex items-center opacity-30">
                    <i className="fas fa-times text-gray-400 w-5 mr-3"></i>
                    <span className="text-sm text-gray-400">Priority Listing Display</span>
                  </div>
                </div>

                {/* Button Section - Fixed Height */}
                <div className="h-12 flex items-end">
                  <Link
                    to="/signup"
                    className="w-full py-3 rounded-xl font-bold text-sm text-white bg-blue-500 hover:bg-blue-600 shadow-lg hover:shadow-xl transition-all duration-300 text-center"
                  >
                    Choose Basic
                  </Link>
                </div>
              </div>

              {/* STARTER Plan */}
              <div className="bg-white rounded-2xl shadow-xl border-2 p-6 relative flex flex-col" style={{ borderColor: '#948979' }}>
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                  <span className="text-white px-4 py-1 rounded-full text-xs font-bold whitespace-nowrap" style={{ backgroundColor: '#948979' }}>
                    MOST POPULAR
                  </span>
                </div>

                {/* Header Section - Fixed Height */}
                <div className="text-center pt-4 h-32 flex flex-col justify-center">
                  <h3 className="text-2xl font-bold mb-2" style={{ color: '#222831' }}>STARTER</h3>
                  <p className="text-sm" style={{ color: '#393E46' }}>Ideal for growing businesses</p>
                </div>

                {/* Price Section - Fixed Height */}
                <div className="text-center h-20 flex flex-col justify-center mb-4">
                  <div>
                    <span className="text-4xl font-bold" style={{ color: '#222831' }}>₹49</span>
                    <span className="text-sm" style={{ color: '#393E46' }}>/one-time</span>
                  </div>
                  <div className="text-xs mt-1" style={{ color: '#948979' }}>
                    Just ₹9.8 per listing
                  </div>
                </div>

                {/* Features Section - Fixed Height */}
                <div className="h-48 space-y-3 mb-6">
                  <div className="flex items-center">
                    <i className="fas fa-check w-5 mr-3" style={{ color: '#948979' }}></i>
                    <span className="text-sm font-medium" style={{ color: '#222831' }}>5 Premium Listings</span>
                  </div>
                  <div className="flex items-center">
                    <i className="fas fa-check w-5 mr-3" style={{ color: '#948979' }}></i>
                    <span className="text-sm" style={{ color: '#222831' }}>30 Days Validity Each</span>
                  </div>
                  <div className="flex items-center">
                    <i className="fas fa-check w-5 mr-3" style={{ color: '#948979' }}></i>
                    <span className="text-sm" style={{ color: '#222831' }}>Priority Support</span>
                  </div>
                  <div className="flex items-center">
                    <i className="fas fa-check w-5 mr-3" style={{ color: '#948979' }}></i>
                    <span className="text-sm" style={{ color: '#222831' }}>Enhanced Visibility</span>
                  </div>
                  <div className="flex items-center">
                    <i className="fas fa-infinity w-5 mr-3" style={{ color: '#948979' }}></i>
                    <span className="text-sm" style={{ color: '#222831' }}>Credits Never Expire</span>
                  </div>
                  <div className="flex items-center opacity-30">
                    <i className="fas fa-times text-gray-400 w-5 mr-3"></i>
                    <span className="text-sm text-gray-400">Priority Listing Display</span>
                  </div>
                </div>

                {/* Button Section - Fixed Height */}
                <div className="h-12 flex items-end">
                  <Link
                    to="/signup"
                    className="w-full py-3 rounded-xl font-bold text-sm text-white shadow-lg hover:shadow-xl transition-all duration-300 text-center"
                    style={{ backgroundColor: '#948979' }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#393E46'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#948979'}
                  >
                    Choose Starter
                  </Link>
                </div>
              </div>

              {/* PRO Plan */}
              <div className="bg-white rounded-2xl shadow-xl border-2 border-gray-200 p-6 relative flex flex-col">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-purple-600 text-white px-4 py-1 rounded-full text-xs font-bold">
                    BEST VALUE
                  </span>
                </div>

                {/* Header Section - Fixed Height */}
                <div className="text-center pt-4 h-32 flex flex-col justify-center">
                  <h3 className="text-2xl font-bold mb-2" style={{ color: '#222831' }}>PRO</h3>
                  <p className="text-sm" style={{ color: '#393E46' }}>Perfect for established businesses</p>
                </div>

                {/* Price Section - Fixed Height */}
                <div className="text-center h-20 flex flex-col justify-center mb-4">
                  <div>
                    <span className="text-4xl font-bold" style={{ color: '#222831' }}>₹99</span>
                    <span className="text-sm" style={{ color: '#393E46' }}>/one-time</span>
                  </div>
                  <div className="text-xs text-purple-600 mt-1">
                    Just ₹9 per listing • Save ₹45
                  </div>
                </div>

                {/* Features Section - Fixed Height */}
                <div className="h-48 space-y-3 mb-6">
                  <div className="flex items-center">
                    <i className="fas fa-check text-purple-600 w-5 mr-3"></i>
                    <span className="text-sm font-medium" style={{ color: '#222831' }}>11 Premium Listings</span>
                  </div>
                  <div className="flex items-center">
                    <i className="fas fa-check text-purple-600 w-5 mr-3"></i>
                    <span className="text-sm" style={{ color: '#222831' }}>30 Days Validity Each</span>
                  </div>
                  <div className="flex items-center">
                    <i className="fas fa-check text-purple-600 w-5 mr-3"></i>
                    <span className="text-sm" style={{ color: '#222831' }}>Premium Support</span>
                  </div>
                  <div className="flex items-center">
                    <i className="fas fa-check text-purple-600 w-5 mr-3"></i>
                    <span className="text-sm" style={{ color: '#222831' }}>Maximum Visibility</span>
                  </div>
                  <div className="flex items-center">
                    <i className="fas fa-infinity text-purple-600 w-5 mr-3"></i>
                    <span className="text-sm" style={{ color: '#222831' }}>Credits Never Expire</span>
                  </div>
                  <div className="flex items-center">
                    <i className="fas fa-star text-purple-600 w-5 mr-3"></i>
                    <span className="text-sm" style={{ color: '#222831' }}>Priority Listing Display</span>
                  </div>
                </div>

                {/* Button Section - Fixed Height */}
                <div className="h-12 flex items-end">
                  <Link
                    to="/signup"
                    className="w-full py-3 rounded-xl font-bold text-sm text-white bg-purple-600 hover:bg-purple-700 shadow-lg hover:shadow-xl transition-all duration-300 text-center"
                  >
                    Choose Pro
                  </Link>
                </div>
              </div>
            </div>

            {/* Call to Action */}
            <div className="text-center">
              <div className="bg-white rounded-2xl shadow-xl p-8 border border-white/20">
                <h3 className="text-2xl font-bold mb-4" style={{ color: '#222831' }}>
                  Ready to get started?
                </h3>
                <p className="mb-6" style={{ color: '#393E46' }}>
                  Join thousands of users who are already sharing, renting, and selling amazing items!
                </p>
                <div className="flex flex-col sm:flex-row justify-center gap-4">
                  <Link
                    to="/signup"
                    className="px-6 py-3 rounded-xl font-bold transition-all duration-300 shadow-lg hover:shadow-xl"
                    style={{ backgroundColor: '#222831', color: 'white' }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#393E46'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#222831'}
                  >
                    <i className="fas fa-user-plus mr-2"></i>Start Free Today
                  </Link>
                  <Link
                    to="/login"
                    className="px-6 py-3 rounded-xl font-bold border-2 transition-all duration-300"
                    style={{ borderColor: '#222831', color: '#222831' }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#222831';
                      e.target.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = '#222831';
                    }}
                  >
                    <i className="fas fa-sign-in-alt mr-2"></i>Already have an account?
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div 
          className="backdrop-blur-sm py-12 sm:py-14 md:py-16 relative overflow-hidden"
          style={{ 
            background: 'rgba(255, 255, 255, 0.8)',
            backgroundImage: 'url(/images/hero-bg.jpeg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          {/* White overlay for content readability */}
          <div className="absolute inset-0" style={{ backgroundColor: 'rgba(255, 255, 255, 0.9)' }}></div>
          
          {/* Subtle pattern overlay */}
          <div 
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: 'linear-gradient(45deg, rgba(148, 137, 121, 0.1) 25%, transparent 25%), linear-gradient(-45deg, rgba(148, 137, 121, 0.1) 25%, transparent 25%), linear-gradient(45deg, transparent 75%, rgba(148, 137, 121, 0.1) 75%), linear-gradient(-45deg, transparent 75%, rgba(148, 137, 121, 0.1) 75%)',
              backgroundSize: '20px 20px',
              backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px'
            }}
          ></div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 text-center">
              <div className="px-2">
                <div className="text-2xl sm:text-3xl md:text-4xl font-bold mb-1 sm:mb-2" style={{ color: '#222831' }}>100%</div>
                <div className="text-sm sm:text-base" style={{ color: '#393E46' }}>Secure Payments</div>
              </div>
              <div className="px-2">
                <div className="text-2xl sm:text-3xl md:text-4xl font-bold mb-1 sm:mb-2" style={{ color: '#393E46' }}>Zero</div>
                <div className="text-sm sm:text-base" style={{ color: '#393E46' }}>Hidden Charges</div>
              </div>
              <div className="px-2">
                <div className="text-2xl sm:text-3xl md:text-4xl font-bold mb-1 sm:mb-2" style={{ color: '#393E46' }}>No</div>
                <div className="text-sm sm:text-base" style={{ color: '#393E46' }}>Limitation on Fashion Categories</div>
              </div>
              <div className="px-2">
                <div className="text-2xl sm:text-3xl md:text-4xl font-bold mb-1 sm:mb-2" style={{ color: '#222831' }}>Free</div>
                <div className="text-sm sm:text-base" style={{ color: '#393E46' }}>Listing (First 3 Items)</div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div 
          className="text-white py-16 sm:py-18 md:py-20 relative overflow-hidden"
          style={{ 
            backgroundColor: '#393E46',
            backgroundImage: 'url(/images/hero-bg.jpeg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          {/* Dark overlay for better text readability */}
          <div className="absolute inset-0" style={{ backgroundColor: 'rgba(57, 62, 70, 0.85)' }}></div>
          
          {/* Gradient overlay for depth */}
          <div 
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(135deg, rgba(34, 40, 49, 0.3) 0%, rgba(57, 62, 70, 0.2) 50%, rgba(148, 137, 121, 0.1) 100%)'
            }}
          ></div>

          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 px-4">Ready to Start?</h2>
            <p className="text-lg sm:text-xl md:text-2xl mb-8 sm:mb-10 md:mb-12 opacity-90 px-4">
              Join thousands of users sharing, renting, and selling fashion sustainably
            </p>
            <Link
              to="/signup"
              className="px-8 sm:px-10 py-3 sm:py-4 rounded-xl font-bold text-lg sm:text-xl transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 inline-block"
              style={{
                background: '#DFD0B8',
                color: '#222831'
              }}
              onMouseEnter={(e) => e.target.style.background = '#d4c5a0'}
              onMouseLeave={(e) => e.target.style.background = '#DFD0B8'}
            >
              <i className="fas fa-star mr-2"></i>Join Now - It's Free!
            </Link>
          </div>
        </div>

        {/* Footer */}
        <Footer />
      </div>
    </>
  );
};

export default Landing;